import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ModalType, VenueType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useExhibitionVenue } from '../../shared/hooks/useApi/useExhibitionVenue';
import { getDefaultVenueFilterData } from '../../shared/util/venueListParams';
import { useLanguageStore } from '../../shared/store/languageStore';
import { LANDING_COLUMN_WIDTH2 } from '../../shared/util/exhibitionVenueIndex';
import { getVenueTypeLabel, getVenueFilterLabel } from '../../shared/util/venueTypeLabel';
import filterIcon from '../../asset/icon/filter.png';

const ExhibitionMetaStation: React.FC = () => {
  const language = useLanguageStore((s) => s.language);
  const { setStandardModal } = useModalStore();
  const { tags, filterData, setFilterData, loadTags } = useExhibitionVenue();
  const didRequestTags = useRef(false);

  useEffect(() => {
    if (didRequestTags.current) return;
    didRequestTags.current = true;
    void loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTypes = Object.entries(filterData.venueType)
    .filter(([, isSelected]) => isSelected)
    .map(([type]) => type as VenueType);

  const handleFilter = () => {
    setStandardModal({
      modalType: ModalType.FILTER_STATION_EXHIBITION,
      title: 'Filter',
      value: '',
      handleChange: () => {},
    });
  };

  const removeFilterFactor = (
    factor: 'admissionFree' | 'openToday',
  ) => {
    let next = filterData;
    switch (factor) {
      case 'admissionFree':
        next = { ...filterData, admissionFree: false };
        break;
      case 'openToday':
        next = { ...filterData, openToday: false };
        break;
    }
    setFilterData(next);
  };

  const removeVenueType = (type: VenueType) => {
    const nextVenueType = {
      ...filterData.venueType,
      [type]: false,
    };
    const hasAnySelected = Object.values(nextVenueType).some(Boolean);
    setFilterData({
      ...filterData,
      venueType: hasAnySelected
        ? nextVenueType
        : getDefaultVenueFilterData().venueType,
    });
  };

  const toggleTag = (tagId: number) => {
    const isSelected = filterData.tagIds.includes(tagId);
    setFilterData({
      ...filterData,
      tagIds: isSelected
        ? filterData.tagIds.filter((id) => id !== tagId)
        : [...filterData.tagIds, tagId],
    });
  };

  const hasTypeFilter = Object.values(filterData.venueType).some((isSelected) => !isSelected);
  const hasAdmissionFilter = filterData.admissionFree;
  const hasOpenTodayFilter = filterData.openToday;

  return (
    <MetaStationComp onClick={(e) => e.stopPropagation()}>
      <FilterGroup>
        <FilterCaption onClick={handleFilter}>
          <IconBtn $backgroundImg={filterIcon} />
          Filter
        </FilterCaption>
        {(hasTypeFilter || hasAdmissionFilter || hasOpenTodayFilter) && (
          <FilterFactorContainer>
            {hasOpenTodayFilter && (
              <FilterFactor onClick={() => removeFilterFactor('openToday')}>
                {getVenueFilterLabel('OPEN_TODAY', language)}
              </FilterFactor>
            )}
            {hasAdmissionFilter && (
              <FilterFactor onClick={() => removeFilterFactor('admissionFree')}>
                {getVenueFilterLabel('FREE', language)}
              </FilterFactor>
            )}
            {hasTypeFilter && (
              <TypeFactorGroup>
                {selectedTypes.map((type) => (
                  <FilterFactor
                    key={type}
                    onClick={() => removeVenueType(type)}
                  >
                    {getVenueTypeLabel(type, language)}
                  </FilterFactor>
                ))}
              </TypeFactorGroup>
            )}
          </FilterFactorContainer>
        )}
      </FilterGroup>
      {tags.length > 0 && (
        <TagContainer>
          {tags.map((tag) => {
            const isSelected = filterData.tagIds.includes(tag.id);
            return (
              <TagFactor
                key={tag.id}
                title={tag.name}
                $isSelected={isSelected}
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
              </TagFactor>
            );
          })}
        </TagContainer>
      )}
    </MetaStationComp>
  );
};

const MetaStationComp = styled.section`
  flex-shrink: 0;
  width: ${LANDING_COLUMN_WIDTH2};
  min-width: ${LANDING_COLUMN_WIDTH2};
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  box-sizing: border-box;

  gap: clamp(100px, 15vh, 200px);

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FilterGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const TagContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const TagFactor = styled.span<{ $isSelected: boolean }>`
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px dotted ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.color_Gray_03 : 'transparent'};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.color_Gray_02 : theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Footer_01};
  user-select: none;
  cursor: pointer;
  transform-origin: left center;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(0.95);
  }
`;

const FilterCaption = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  padding: 3px 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  gap: 4px;
`;

const FilterFactorContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const TypeFactorGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const FilterFactor = styled.div`
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.color_Gray_05};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Footer_01};
  user-select: none;
  cursor: pointer;
`;

const IconBtn = styled.div<{ $backgroundImg: string }>`
  width: clamp(10px, 2vw, 12px);
  height: clamp(10px, 2vw, 12px);
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;

export default ExhibitionMetaStation;
